/**
 * Forwarding (protocol polls.ts, 2026-10-29), as Discord does it: Forward on
 * a message's menu opens a picker of the channels and DMs you can send to
 * (search, up to 5, an optional message sent after the forward), and a
 * forwarded message shows "Forwarded", the copy, and a link to the original
 * when you can see where it is. The dialog is a bottom sheet on a phone.
 */
import {
  ChannelType,
  FORWARD_MAX_TARGETS,
  MessageType,
  normalizeForSearch,
  Permissions,
  RelationshipType,
  compareSnowflakes,
  type Channel,
  type Message,
  type Snowflake,
  type User,
} from '@neosocial/protocol';
import { useMemo, useState, type ReactNode } from 'react';
import { create } from 'zustand';
import { useT } from '../i18n';
import { useFlag } from '../store/config';
import { useData } from '../store/data';
import { forwardMessage } from '../store/polls';
import {
  can,
  channelPermissions,
  channelsInSidebarOrder,
  displayName,
  privateChannelName,
  userName,
} from '../store/selectors';
import { goHub, useUi } from '../store/ui';
import { errorText } from '../mod/actions';
import { Avatar, Btn, Icon } from '../ui/kit';
import { Dialog, toast } from '../ui/overlay';
import { plainText, renderMarkdown, type MentionResolver } from './markdown';

/** Whether a message can be forwarded: someone's text or images (or a forward), not a poll or a system line. */
export function canForward(m: Message): boolean {
  if (m.type !== MessageType.DEFAULT && m.type !== MessageType.REPLY) return false;
  if (m.poll) return false;
  return !!m.content.trim() || m.attachments.length > 0 || !!m.message_snapshots?.length;
}

/** The message a forward shows: its snapshot, else the message itself. */
function shown(m: Message): { content: string; images: number } {
  const s = m.message_snapshots?.[0]?.message;
  return s
    ? { content: s.content, images: s.attachments.length }
    : { content: m.content, images: m.attachments.length };
}

const useForward = create<{ m: Message | null }>(() => ({ m: null }));

/** Opens the Forward picker for `m` (the message's menu). */
export function openForward(m: Message): void {
  useForward.setState({ m });
}

/** Where the Forward picker opens (mounted once, in App). */
export function ForwardHost() {
  const m = useForward((s) => s.m);
  const on = useFlag('message_forwarding');
  if (!m || !on) return null;
  return <ForwardDialog m={m} onClose={() => useForward.setState({ m: null })} />;
}

interface Target {
  channel: Channel;
  name: string;
  sub: string;
  /** A 1:1 DM's other person (their avatar). */
  peer?: User;
}

/**
 * The places you may forward to: text channels where you can send (and
 * attach, when it carries images), and your DMs and groups (not one with
 * someone you blocked), the most recent first.
 */
function useTargets(images: boolean): Target[] {
  const { t } = useT();
  const channels = useData((d) => d.channels);
  const members = useData((d) => d.members);
  const guilds = useData((d) => d.guilds);
  const relationships = useData((d) => d.relationships);
  const meId = useData((d) => d.me?.id);
  return useMemo(() => {
    // the members (their roles) decide what you may send in: the list follows them
    const d = { ...useData.getState(), members };
    const need = Permissions.VIEW_CHANNEL | Permissions.SEND_MESSAGES;
    const out: Target[] = [];
    for (const gid of Object.keys(guilds)) {
      for (const c of channelsInSidebarOrder({ channels }, gid)) {
        if (c.type !== ChannelType.GUILD_TEXT && c.type !== ChannelType.GUILD_ANNOUNCEMENT)
          continue;
        const p = channelPermissions(d, c);
        if (!can(p, need) || (images && !can(p, Permissions.ATTACH_FILES))) continue;
        out.push({
          channel: c,
          name: c.name ?? '',
          sub: c.parent_id ? (channels[c.parent_id]?.name ?? '') : '',
        });
      }
    }
    for (const c of Object.values(channels)) {
      if (c.type !== ChannelType.DM && c.type !== ChannelType.GROUP_DM) continue;
      if (c.nx_request_state && c.nx_request_state !== 'accepted') continue;
      const peer = c.type === ChannelType.DM ? c.recipients?.find((u) => u.id !== meId) : undefined;
      if (peer && relationships[peer.id]?.type === RelationshipType.BLOCKED) continue;
      out.push({
        channel: c,
        name: privateChannelName(d, c),
        sub: t(c.type === ChannelType.DM ? 'fw.dm' : 'fw.group'),
        ...(peer ? { peer } : {}),
      });
    }
    // the most recent first; channels nobody wrote in keep the sidebar's order at the end
    return out
      .map((x, i) => ({ x, i }))
      .sort((a, b) => {
        const la = a.x.channel.last_message_id;
        const lb = b.x.channel.last_message_id;
        if (la && lb) return compareSnowflakes(lb, la);
        if (la || lb) return la ? -1 : 1;
        return a.i - b.i;
      })
      .map((y) => y.x);
    // members and guilds change what you may send in
  }, [channels, members, guilds, relationships, meId, images, t]);
}

function ForwardDialog({ m, onClose }: { m: Message; onClose(): void }) {
  const { t } = useT();
  const what = shown(m);
  const targets = useTargets(what.images > 0);
  const author = useData((d) => userName(d, m.author.id, m.guild_id));
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<Snowflake[]>([]);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const query = normalizeForSearch(q.trim().replace(/^[#@]/, ''));
  const list = query
    ? targets.filter(
        (x) =>
          normalizeForSearch(x.name).includes(query) || normalizeForSearch(x.sub).includes(query),
      )
    : targets;
  const full = picked.length >= FORWARD_MAX_TARGETS;
  const send = async () => {
    if (!picked.length || busy) return;
    setBusy(true);
    const { ok, error } = await forwardMessage(m, picked, comment);
    setBusy(false);
    if (ok === picked.length) {
      toast(t('fw.sent', { n: ok }), { icon: 'forward' });
      onClose();
    } else if (ok > 0) {
      toast(t('fw.partial', { ok, n: picked.length, err: errorText(error) }), {
        icon: 'alert',
        tone: 'warn',
      });
      onClose();
    } else toast(errorText(error), { icon: 'alert', tone: 'danger' });
  };
  return (
    <Dialog
      title={t('fw.title')}
      icon="forward"
      tone="mode"
      size="md"
      cls="fw-dlg"
      onClose={onClose}
      actions={
        <>
          <Btn size="sm" onClick={onClose}>
            {t('c.cancel')}
          </Btn>
          <Btn
            kind="primary"
            size="sm"
            busy={busy}
            disabled={!picked.length || busy}
            onClick={() => void send()}
          >
            {t('fw.send')}
          </Btn>
        </>
      }
    >
      <blockquote className="fw-prev">
        <b dir="auto">{author}</b>
        <span dir="auto">
          {what.content ? plainText(what.content, NAMES) : t('fw.images', { n: what.images })}
        </span>
      </blockquote>
      <input
        className="nx-input"
        dir="auto"
        placeholder={t('fw.search')}
        aria-label={t('fw.search')}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        data-autofocus
      />
      <p className="fw-hint ns-help" aria-live="polite">
        {full
          ? t('fw.max', { n: FORWARD_MAX_TARGETS })
          : picked.length
            ? t('fw.picked', { n: picked.length })
            : ' '}
      </p>
      <ul className="ns-pick fw-pick nx-scroll">
        {list.map((x) => {
          const on = picked.includes(x.channel.id);
          return (
            <li key={x.channel.id}>
              <label>
                <input
                  type="checkbox"
                  checked={on}
                  disabled={!on && full}
                  onChange={() =>
                    setPicked((l) =>
                      on ? l.filter((id) => id !== x.channel.id) : [...l, x.channel.id],
                    )
                  }
                />
                {x.peer ? (
                  <Avatar user={x.peer} size={28} />
                ) : (
                  <span className="fw-ic" aria-hidden="true">
                    <Icon
                      name={x.channel.type === ChannelType.GROUP_DM ? 'users' : 'hash'}
                      size="s"
                    />
                  </span>
                )}
                <span className="fw-t">
                  <span dir="auto">{x.name}</span>
                  {x.sub ? <small dir="auto">{x.sub}</small> : null}
                </span>
              </label>
            </li>
          );
        })}
        {!list.length ? (
          <li className="ns-muted">{q.trim() ? t('fw.none', { q: q.trim() }) : t('fw.empty')}</li>
        ) : null}
      </ul>
      <textarea
        className="nx-textarea fw-note"
        dir="auto"
        rows={2}
        maxLength={2000}
        placeholder={t('fw.comment')}
        aria-label={t('fw.comment')}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
      />
    </Dialog>
  );
}

/** The preview's mentions as names, from what the store knows. */
const NAMES: Pick<MentionResolver, 'user' | 'role' | 'channel'> = {
  user: (id) => {
    const u = useData.getState().users[id];
    return u ? displayName(u) : null;
  },
  role: (id) => {
    for (const g of Object.values(useData.getState().guilds)) {
      const r = g.roles.find((x) => x.id === id);
      if (r) return { name: r.name, color: r.color };
    }
    return null;
  },
  channel: (id) => useData.getState().channels[id]?.name ?? null,
};

/** Shows the original message in its own channel (a channel, a voice channel's chat, a DM). */
function goToMessage(c: Channel, messageId: Snowflake): void {
  if (c.type === ChannelType.DM || c.type === ChannelType.GROUP_DM)
    goHub({ kind: 'home', channelId: c.id });
  else if (c.type === ChannelType.GUILD_VOICE || c.type === ChannelType.GUILD_STAGE_VOICE)
    goHub({ kind: 'voice', channelId: c.id, chat: true });
  else goHub({ kind: 'channel', channelId: c.id });
  useUi.getState().set({ jump: { channelId: c.id, messageId } });
}

/**
 * A forwarded message: "Forwarded", the copy (text and images), and below it
 * where it came from and when, a link when you can see that channel.
 */
export function Forwarded({
  m,
  resolver,
  media,
  onJump,
}: {
  m: Message;
  resolver: MentionResolver;
  media: ReactNode;
  onJump(id: Snowflake): void;
}) {
  const { t, fmt } = useT();
  const snap = m.message_snapshots![0]!.message;
  const ref = m.message_reference;
  const src = useData((d) => (ref?.channel_id ? d.channels[ref.channel_id] : undefined));
  const visible = useData(
    (d) => !!src && can(channelPermissions(d, src), Permissions.VIEW_CHANNEL),
  );
  const srcName = useData((d) =>
    src
      ? src.type === ChannelType.DM || src.type === ChannelType.GROUP_DM
        ? privateChannelName(d, src)
        : '#' + (src.name ?? '')
      : '',
  );
  const when = fmt.stamp(snap.timestamp);
  return (
    <div className="fw">
      <span className="fw-tag">
        <Icon name="forward" size="s" />
        {t('fw.tag')}
      </span>
      {snap.content ? (
        <div className="msg-text" dir="auto">
          {renderMarkdown(snap.content, resolver, t('hub.spoiler'))}
          {snap.edited_timestamp ? (
            <>
              {' '}
              <span className="msg-edited">{t('hub.msg.edited')}</span>
            </>
          ) : null}
        </div>
      ) : null}
      {media}
      {visible && ref ? (
        <button
          type="button"
          className="fw-src"
          aria-label={t('fw.from')}
          data-tip={t('fw.from')}
          onClick={() =>
            ref.channel_id === m.channel_id
              ? onJump(ref.message_id)
              : goToMessage(src!, ref.message_id)
          }
        >
          <bdi>{srcName}</bdi>
          <span aria-hidden="true">·</span>
          <time dateTime={snap.timestamp}>{when}</time>
          <Icon name="chev-right" size="s" />
        </button>
      ) : (
        <span className="fw-src is-plain">
          <time dateTime={snap.timestamp}>{when}</time>
        </span>
      )}
    </div>
  );
}
